import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { ReactNode, Suspense, useCallback, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { ClubStackNavigatorParamList } from "../../../../navigators/ClubNavigator";
import { UnauthenticatedNavigatorParamList } from "../../../../navigators/UnauthenticatedStackNavigator/types";
import { AuthenticationModalScreen } from "./AuthenticationModalScreen";
import { AuthenticateBeforeProceedScreenQuery } from "./__generated__/AuthenticateBeforeProceedScreenQuery.graphql";

/**
 * This component can wrap any other component and will force
 * the user to have a valid member profile for the current club
 * before it renders its children.
 *
 * **NOTE** this conditionally renders its children, so do not include
 * navigators within here or you will end up with issues conditionally
 * rendering navigators
 */
export const AuthenticateBeforeProceedScreen = ({
  children,
  onAuthenticationSuccess,
}: {
  children: ReactNode;
  onAuthenticationSuccess?: () => void;
}) => {
  const [queryRef, loadQuery] =
    useQueryLoader<AuthenticateBeforeProceedScreenQuery>(
      AuthenticateBeforeProceedScreenQueryString
    );

  useEffect(() => {
    loadQuery({}, { fetchPolicy: "store-and-network" });
  }, [loadQuery]);
  return (
    <Suspense>
      {queryRef ? (
        <AuthenticateBeforeProceedQueryChecker
          queryRef={queryRef}
          refreshQueryHandler={() => {
            loadQuery({}, { fetchPolicy: "network-only" });
            onAuthenticationSuccess?.();
          }}
        >
          {children}
        </AuthenticateBeforeProceedQueryChecker>
      ) : null}
    </Suspense>
  );
};

const AuthenticateBeforeProceedQueryChecker = ({
  queryRef,
  children,
  refreshQueryHandler,
}: {
  queryRef: PreloadedQuery<AuthenticateBeforeProceedScreenQuery>;
  children: ReactNode;
  refreshQueryHandler: () => void;
}) => {
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const unauthenticatedNavigation =
    useNavigation<NavigationProp<UnauthenticatedNavigatorParamList>>();

  const data = usePreloadedQuery(
    AuthenticateBeforeProceedScreenQueryString,
    queryRef
  );
  const validAnonymousUserFacilitySlug =
    data.session?.currentClientProfile?.member?.club.facilities?.edges?.find(
      (e) => e?.node?.publicBookingPermissionSet?.isFacilityVisible
    )?.node?.slug ?? null;

  const onCloseHandler = useCallback(() => {
    /**
     * If we are looking at a club with a valid public facility
     * and the user cancel's auth, just send them there.
     */
    if (validAnonymousUserFacilitySlug) {
      navigation.navigate("Agenda", {
        facilitySlug: validAnonymousUserFacilitySlug,
        date: "today",
      });
    } else {
      /**
       * Otherwise this must be a private facility so gee whiz,
       * I guess we gotta ship this guy all the back back home
       */
      unauthenticatedNavigation.navigate("Initial");
    }
  }, [navigation, unauthenticatedNavigation, validAnonymousUserFacilitySlug]);

  const hasCurrentValidProfile =
    data.session?.currentClientProfile?.__typename === "MemberProfile";

  return hasCurrentValidProfile ? (
    <>{children}</>
  ) : (
    <AuthenticationModalScreen
      onComplete={refreshQueryHandler}
      onCloseHandler={onCloseHandler}
      clubSlug={data.session?.currentClientProfile?.member?.club.slug ?? ""}
    />
  );
};

export const AuthenticateBeforeProceedScreenQueryString = graphql`
  query AuthenticateBeforeProceedScreenQuery {
    session {
      currentClientProfile {
        __typename
        member {
          club {
            slug
            facilities {
              edges {
                node {
                  slug
                  publicBookingPermissionSet {
                    isFacilityVisible
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
